import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SUBMISSION_DISPLAY_STATUS_LABELS } from '@tacobots-forms/shared';
import type { FormStatus, SubmissionDisplayStatus } from '@tacobots-forms/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';

/** Page title with optional description and right-aligned actions. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {breadcrumb}
        <Typography variant="h1" sx={{ wordBreak: 'break-word' }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return (
    <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', py: 8 }}>
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * Renders whatever an API error carries, without ever guessing: the message
 * comes from the server, which is written to be safe to show.
 */
export function ErrorState({ error, action }: { error: unknown; action?: ReactNode }) {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? 'Ocurrió un error. Inténtelo de nuevo.'
        : 'Ocurrió un error. Inténtelo de nuevo.';

  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <Alert severity="error" action={action} sx={{ my: 2 }}>
      <Typography variant="body2">{message}</Typography>
      {requestId ? (
        <Typography variant="caption" color="text.secondary">
          Referencia: {requestId}
        </Typography>
      ) : null}
    </Alert>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
      <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
        {icon}
        <Typography variant="h3">{title}</Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
            {description}
          </Typography>
        ) : null}
        {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
      </Stack>
    </Paper>
  );
}

const FORM_STATUS_STYLE: Record<FormStatus, { label: string; color: ChipColor }> = {
  DRAFT: { label: 'Borrador', color: 'default' },
  PUBLISHED: { label: 'Publicado', color: 'success' },
  CLOSED: { label: 'Cerrado', color: 'warning' },
  ARCHIVED: { label: 'Archivado', color: 'default' },
};

/**
 * Only the colour lives here: the words are shared with the API, which says
 * them in the CSV export too, and a word kept in two places drifts.
 *
 * "Solo se identificó" is not "En proceso": nothing is in progress. The person
 * typed their name and address and stopped there, which is what most unfinished
 * rows really are.
 */
const SUBMISSION_STATUS_COLOR: Record<SubmissionDisplayStatus, ChipColor> = {
  IDENTIFIED_ONLY: 'default',
  STARTED: 'info',
  PROCESSING: 'info',
  COMPLETED: 'success',
  CANCELLED: 'default',
  INVALIDATED: 'error',
};

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

export function FormStatusChip({ status }: { status: FormStatus }) {
  const style = FORM_STATUS_STYLE[status];
  return <Chip size="small" label={style.label} color={style.color} variant="outlined" />;
}

export function SubmissionStatusChip({ status }: { status: SubmissionDisplayStatus }) {
  return (
    <Chip
      size="small"
      label={SUBMISSION_DISPLAY_STATUS_LABELS[status]}
      color={SUBMISSION_STATUS_COLOR[status]}
      variant="outlined"
    />
  );
}

/** The same labels, for anything that is not a chip. */
export function submissionStatusLabel(status: SubmissionDisplayStatus): string {
  return SUBMISSION_DISPLAY_STATUS_LABELS[status];
}

/** Copies text and confirms it, because a share link is worthless half-copied. */
export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the field next to the button is
      // selectable, so there is always a manual way out.
      setCopied(false);
    }
  }, [value]);

  return (
    <>
      <Tooltip title={label}>
        <IconButton onClick={copy} size="small" aria-label={label}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Copiado al portapapeles"
      />
    </>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  destructive = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{description}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
